import styles from "./CdLoja.module.scss";
import React,{useEffect,useState} from "react";
import {Store} from "lucide-react";
import {useLocation,useNavigate} from "react-router-dom";
import PageContainer from "shared/components/pagecontainer/PageContainer";
import {RepresentanteLegal} from "shared/components/etapasCadastro/Loja/RepresentanteLegal";
import {Endereco} from "shared/components/etapasCadastro/Loja/Endereco";
import {DadosBancarios} from "shared/components/etapasCadastro/Loja/DadosBancarios";
import {DadosLoja,BusinessHoursSchedule} from "shared/components/etapasCadastro/Loja/DadosLoja";
import {CadastroRealizado} from "shared/components/modal/CadastroRealizado";
import {criarNovaLoja} from "shared/services/api/lojas.api";
import {traduzirErro} from "shared/utils/traduzirErro";

type Aba="informacoes"|"representante"|"endereco"|"bancarios";

interface InformacoesData{
  loja:string;
  company_name:string;
  segmento:string;
  cnpj:string;
  telefone:string;
  email:string;
  annual_revenue:number;
  descricao?:string;
  businessHoursSchedule:BusinessHoursSchedule;
}

interface RepresentanteData{
  mother_name:string;
  monthly_income:number;
  professional_occupation:string;
  self_declared_legal_representative:boolean;
  addressId:string;
}

interface EnderecoData{
  cep:string;
  cidade:string;
  estado:string;
  endereco:string;
  numero:string;
  bairro:string;
  completo?:string;
  pontoReferencia?:string;
}

interface DadosBancariosData{
  banco:string;
  agencia:string;
  conta:string;
  tipoConta:string;
}

interface CadastroLojaState{
  origem?:string;
  returnTo?:string;
  selecionarEndereco?:boolean;
  enderecoId?:string;
  dadosInformacoes?:InformacoesData|null;
  dadosRepresentante?:RepresentanteData|null;
  dadosEndereco?:EnderecoData|null;
  abaRetorno?:Aba;
}

export const CdLoja:React.FC=()=>{
  const navigate=useNavigate();
  const location=useLocation();
  const [abaAtual,setAbaAtual]=useState<Aba>("informacoes");
  const [dadosInformacoes,setDadosInformacoes]=useState<InformacoesData|null>(null);
  const [dadosRepresentante,setDadosRepresentante]=useState<RepresentanteData|null>(null);
  const [dadosEndereco,setDadosEndereco]=useState<EnderecoData|null>(null);
  const [salvando,setSalvando]=useState(false);
  const [erroCadastro,setErroCadastro]=useState<string|null>(null);
  const [lojaCadastrada,setLojaCadastrada]=useState(false);

  useEffect(()=>{
    const state=location.state as CadastroLojaState|null;
    if(!state||state.origem!=="cadastro-loja")return;

    if(state.dadosInformacoes)setDadosInformacoes(state.dadosInformacoes);
    if(state.dadosRepresentante)setDadosRepresentante(state.dadosRepresentante);
    if(state.dadosEndereco)setDadosEndereco(state.dadosEndereco);

    if(state.enderecoId&&state.dadosRepresentante){
      setDadosRepresentante({...state.dadosRepresentante,addressId:state.enderecoId});
    }

    if(state.abaRetorno)setAbaAtual(state.abaRetorno);
  },[location.state]);

  const tituloEtapa:Record<Aba,string>={
    informacoes:"Informações da loja",
    representante:"Representante legal",
    endereco:"Endereço da loja",
    bancarios:"Dados bancários",
  };

  const descricaoEtapa:Record<Aba,string>={
    informacoes:"Preencha os dados da sua loja.",
    representante:"Informe os dados do representante legal da empresa.",
    endereco:"Informe o endereço da sua loja e mantenha seus dados atualizados.",
    bancarios:"Forneça seus dados bancários corretamente para receber seus pagamentos.",
  };

  const salvarEstadoCadastro=(dados:{
    dadosInformacoes?:InformacoesData|null;
    dadosRepresentante?:RepresentanteData|null;
    dadosEndereco?:EnderecoData|null;
    abaRetorno:Aba;
  })=>{
    navigate(location.pathname,{
      replace:true,
      state:{
        origem:"cadastro-loja",
        returnTo:location.pathname,
        selecionarEndereco:true,
        dadosInformacoes:dados.dadosInformacoes!==undefined?dados.dadosInformacoes:dadosInformacoes,
        dadosRepresentante:dados.dadosRepresentante!==undefined
          ?dados.dadosRepresentante:dadosRepresentante,
        dadosEndereco:dados.dadosEndereco!==undefined?dados.dadosEndereco:dadosEndereco,
        abaRetorno:dados.abaRetorno,
      } as CadastroLojaState,
    });
  };

  const mudarAba=(aba:Aba)=>{
    if(aba==="informacoes"){
      setErroCadastro(null);
      setAbaAtual("informacoes");
      return;
    }

    if(aba==="representante"&&dadosInformacoes){
      setErroCadastro(null);
      setAbaAtual("representante");
      return;
    }

    if(aba==="endereco"&&dadosInformacoes&&dadosRepresentante){
      if(dadosRepresentante.self_declared_legal_representative!==true){
        setErroCadastro("É necessário confirmar a responsabilidade como representante legal.");
        setAbaAtual("representante");
        return;
      }
      setErroCadastro(null);
      setAbaAtual("endereco");
      return;
    }

    if(aba==="bancarios"&&dadosInformacoes&&dadosRepresentante&&dadosEndereco){
      if(dadosRepresentante.self_declared_legal_representative!==true){
        setErroCadastro("É necessário confirmar a responsabilidade como representante legal.");
        setAbaAtual("representante");
        return;
      }
      setErroCadastro(null);
      setAbaAtual("bancarios");
    }
  };

  const voltar=()=>{
    setErroCadastro(null);

    if(abaAtual==="informacoes"){
      navigate(-1);
      return;
    }

    if(abaAtual==="representante"){
      setAbaAtual("informacoes");
      return;
    }

    if(abaAtual==="endereco"){
      setAbaAtual("representante");
      return;
    }

    setAbaAtual("endereco");
  };

  const handleInformacoes=(data:InformacoesData)=>{
    setDadosInformacoes(data);
    setErroCadastro(null);
    salvarEstadoCadastro({dadosInformacoes:data,abaRetorno:"representante"});
    setAbaAtual("representante");
  };

  const handleRepresentante=(data:RepresentanteData)=>{
    if(data.self_declared_legal_representative!==true){
      setErroCadastro("É necessário confirmar a responsabilidade como representante legal.");
      setAbaAtual("representante");
      return;
    }

    setDadosRepresentante(data);
    setErroCadastro(null);
    salvarEstadoCadastro({dadosRepresentante:data,abaRetorno:"endereco"});
    setAbaAtual("endereco");
  };

  const handleEndereco=(data:EnderecoData)=>{
    if(!dadosRepresentante||dadosRepresentante.self_declared_legal_representative!==true){
      setErroCadastro("É necessário confirmar a responsabilidade como representante legal.");
      setAbaAtual("representante");
      return;
    }

    setDadosEndereco(data);
    setErroCadastro(null);
    salvarEstadoCadastro({dadosEndereco:data,abaRetorno:"bancarios"});
    setAbaAtual("bancarios");
  };

  const cadastrarLoja=async(data:DadosBancariosData)=>{
    if(salvando)return;

    if(!dadosInformacoes){
      setErroCadastro("As informações da loja não foram preenchidas.");
      setAbaAtual("informacoes");
      return;
    }

    if(!dadosRepresentante){
      setErroCadastro("Os dados do representante legal não foram preenchidos.");
      setAbaAtual("representante");
      return;
    }

    if(dadosRepresentante.self_declared_legal_representative!==true){
      setErroCadastro("É necessário confirmar a responsabilidade como representante legal.");
      setAbaAtual("representante");
      return;
    }

    if(!dadosEndereco){
      setErroCadastro("O endereço da loja não foi preenchido.");
      setAbaAtual("endereco");
      return;
    }

    setSalvando(true);
    setErroCadastro(null);

    try{
      const address={
        street:dadosEndereco.endereco.trim(),
        number:dadosEndereco.numero.trim(),
        neighborhood:dadosEndereco.bairro.trim(),
        city:dadosEndereco.cidade.trim(),
        state:dadosEndereco.estado.trim(),
        zipCode:dadosEndereco.cep.replace(/\D/g,""),
        complement:dadosEndereco.completo?.trim()??"",
        referencePoint:dadosEndereco.pontoReferencia?.trim()??"",
      };

      const businessHours:BusinessHoursSchedule={
        MON:dadosInformacoes.businessHoursSchedule.MON,
        TUE:dadosInformacoes.businessHoursSchedule.TUE,
        WED:dadosInformacoes.businessHoursSchedule.WED,
        THU:dadosInformacoes.businessHoursSchedule.THU,
        FRI:dadosInformacoes.businessHoursSchedule.FRI,
        SAT:dadosInformacoes.businessHoursSchedule.SAT,
        SUN:dadosInformacoes.businessHoursSchedule.SUN,
      };

      const contaInformada=data.conta.trim().toUpperCase();
      const contaMatch=contaInformada.match(/^(.+?)[-\s]([0-9X])$/i);

      if(!contaMatch){
        throw new Error("Informe a conta com o dígito, por exemplo: 1266365-4.");
      }

      const banking={
        bankCode:String(data.banco).replace(/\D/g,"").padStart(3,"0"),
        branch:data.agencia.trim(),
        branchDigit:null,
        account:contaMatch[1].trim(),
        accountDigit:contaMatch[2].toUpperCase(),
        type:data.tipoConta==="poupanca"?("savings" as const):("checking" as const),
      };

      const lojaId=await criarNovaLoja({
        name:dadosInformacoes.loja.trim(),
        companyName:dadosInformacoes.company_name.trim(),
        cnpj:dadosInformacoes.cnpj.replace(/\D/g,""),
        phone:dadosInformacoes.telefone.replace(/\D/g,""),
        email:dadosInformacoes.email.trim(),
        categoryId:dadosInformacoes.segmento,
        description:dadosInformacoes.descricao?.trim()??"",
        annualRevenue:dadosInformacoes.annual_revenue,
        legalRepresentative:{
          addressId:dadosRepresentante.addressId,
          motherName:dadosRepresentante.mother_name.trim(),
          monthlyIncome:dadosRepresentante.monthly_income,
          professionalOccupation:dadosRepresentante.professional_occupation.trim(),
          selfDeclaredLegalRepresentative:dadosRepresentante.self_declared_legal_representative,
        },
        address,
        businessHours,
        banking,
      });

      if(!lojaId){
        throw new Error("A API não retornou o ID da loja criada.");
      }

      setLojaCadastrada(true);
    }catch(error:any){
      setErroCadastro(traduzirErro(error));
    }finally{
      setSalvando(false);
    }
  };

  return(
    <PageContainer title="Cadastrar Loja" icon={<Store size={20}/>} showDesktopSearch={false}>
      <div className={styles.page}>
        <div className={styles.card}>
          <div className={styles.formHeader}>
            <div>
              <h2>{tituloEtapa[abaAtual]}</h2>
              <p>{descricaoEtapa[abaAtual]}</p>
            </div>
          </div>

          <div className={styles.breadcrumbs}>
            <button type="button" className={`${styles.breadcrumb} ${
              abaAtual==="informacoes"?styles.breadcrumbAtivo:styles.breadcrumbConcluido
            }`} onClick={()=>mudarAba("informacoes")}>Informações</button>
            <span className={styles.separador}>/</span>
            <button type="button" className={`${styles.breadcrumb} ${
              abaAtual==="representante"?styles.breadcrumbAtivo:
              abaAtual==="endereco"||abaAtual==="bancarios"?styles.breadcrumbConcluido:""
            }`} onClick={()=>mudarAba("representante")}>Representante Legal</button>
            <span className={styles.separador}>/</span>
            <button type="button" className={`${styles.breadcrumb} ${
              abaAtual==="endereco"?styles.breadcrumbAtivo:
              abaAtual==="bancarios"?styles.breadcrumbConcluido:""
            }`} onClick={()=>mudarAba("endereco")}>Endereço</button>
            <span className={styles.separador}>/</span>
            <button type="button" className={`${styles.breadcrumb} ${
              abaAtual==="bancarios"?styles.breadcrumbAtivo:""
            }`} onClick={()=>mudarAba("bancarios")}>Dados Bancários</button>
          </div>

          {erroCadastro&&(
            <div style={{
              marginBottom:"20px",padding:"12px 16px",borderRadius:"8px",backgroundColor:"#fff1f2",
              border:"1px solid #fecdd3",color:"#be123c",whiteSpace:"pre-line",fontSize:"14px",
            }}>
              {erroCadastro}
            </div>
          )}

          <div className={styles.tabContent}>
            {abaAtual==="informacoes"&&(
              <DadosLoja onNext={handleInformacoes} defaultValues={dadosInformacoes??undefined}/>
            )}

            {abaAtual==="representante"&&(
              <RepresentanteLegal onNext={handleRepresentante} defaultValues={dadosRepresentante??undefined}/>
            )}

            {abaAtual==="endereco"&&(
              <Endereco onNext={handleEndereco}/>
            )}

            {abaAtual==="bancarios"&&(
              <DadosBancarios onNext={cadastrarLoja}/>
            )}
          </div>

          <div className={styles.navigation}>
            <button type="button" className={styles.btnCancelar} onClick={voltar} disabled={salvando}>
              {abaAtual==="informacoes"?"Cancelar":"Voltar"}
            </button>

            <div className={styles.actionsRight}>
              {abaAtual==="informacoes"&&(
                <button type="submit" form="informacoes-loja-form" className={styles.btnProximo}
                  disabled={salvando}>Próximo</button>
              )}

              {abaAtual==="representante"&&(
                <button type="submit" form="representante-legal-form" className={styles.btnProximo}
                  disabled={salvando}>Próximo</button>
              )}

              {abaAtual==="endereco"&&(
                <button type="submit" form="endereco-loja-form" className={styles.btnProximo}
                  disabled={salvando}>Próximo</button>
              )}

              {abaAtual==="bancarios"&&(
                <button type="submit" form="dados-bancarios-loja-form" className={styles.btnSalvar}
                  disabled={salvando}>{salvando?"Cadastrando...":"Cadastrar Loja"}</button>
              )}
            </div>
          </div>
        </div>
      </div>

      <CadastroRealizado
        open={lojaCadastrada}
        mensagem="Sua loja foi cadastrada com sucesso!"
        descricao="Sua loja foi cadastrada e já está disponível em Minhas Lojas."
        detalheLabel="Loja"
        detalheValor={dadosInformacoes?.loja}
        textoBotao="Minhas lojas"
        onClose={()=>setLojaCadastrada(false)}
        onContinuar={()=>navigate("/minhaslojas")}
      />
    </PageContainer>
  );
};